import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, MessageCircle, Info } from 'lucide-react'
import { useCart } from '../context/CartContext'
import { useAuth } from '../context/AuthContext'
import toast from 'react-hot-toast'

const Cart = () => {
  const { cart, removeFromCart, updateQuantity, cartTotal, cartCount } = useCart()
  const { user } = useAuth()
  const navigate = useNavigate()

  const shipping = cartCount <= 3 ? 0 : 150
  const tax = 0
  const total = cartTotal + shipping + tax

  const handleWhatsAppCheckout = () => {
    if (!user) {
      toast.error('Please login to checkout')
      navigate('/login')
      return
    }
    // Format cart items for WhatsApp message
    const itemsList = cart.map(item => 
      `• ${item.name} (${item.category})\n  Quantity: ${item.quantity}\n  Price: ₹${(item.price * item.quantity).toLocaleString()}`
    ).join('\n\n')

    const message = `Hello Daisy Jewells! 💎

I would like to place an order:

${itemsList}

*Customer:* ${user.full_name}
*Email:* ${user.email}

*Order Summary:*
Subtotal: ₹${cartTotal.toLocaleString()}
Shipping: ${shipping === 0 ? 'FREE' : `₹${shipping}`}
Tax (GST 3%): ₹${tax.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
*Total: ₹${total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}*

Please confirm my order. Thank you! 🌸`

    const whatsappUrl = `https://wa.me/918369192251?text=${encodeURIComponent(message)}`
    window.open(whatsappUrl, '_blank')
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-20" data-component-path="client/src/pages/Cart.jsx" data-component-line="42:6" data-component-id="client/src/pages/Cart.jsx:42:6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="client/src/pages/Cart.jsx" data-component-line="43:8" data-component-id="client/src/pages/Cart.jsx:43:8">
          <div className="text-center" data-component-path="client/src/pages/Cart.jsx" data-component-line="44:10" data-component-id="client/src/pages/Cart.jsx:44:10">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6" data-component-path="client/src/pages/Cart.jsx" data-component-line="45:12" data-component-id="client/src/pages/Cart.jsx:45:12">
              <ShoppingBag className="w-12 h-12 text-gray-400" data-component-path="client/src/pages/Cart.jsx" data-component-line="46:14" data-component-id="client/src/pages/Cart.jsx:46:14" />
            </div>
            <h2 className="font-serif text-3xl font-bold text-gray-800 mb-4" data-component-path="client/src/pages/Cart.jsx" data-component-line="48:12" data-component-id="client/src/pages/Cart.jsx:48:12">Your Cart is Empty</h2>
            <p className="text-gray-600 mb-8" data-component-path="client/src/pages/Cart.jsx" data-component-line="49:12" data-component-id="client/src/pages/Cart.jsx:49:12">
              Discover our beautiful collection of handcrafted earrings
            </p>
            <Link 
              to="/shop"
              className="inline-flex items-center justify-center gap-2 bg-champagne-600 text-white px-8 h-14 rounded-full hover:bg-champagne-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
              data-component-path="client/src/pages/Cart.jsx" data-component-line="53:14" data-component-id="client/src/pages/Cart.jsx:53:14"
            >
              Start Shopping
              <ArrowRight className="w-5 h-5" data-component-path="client/src/pages/Cart.jsx" data-component-line="58:14" data-component-id="client/src/pages/Cart.jsx:58:14" />
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-component-path="client/src/pages/Cart.jsx" data-component-line="66:4" data-component-id="client/src/pages/Cart.jsx:66:4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="client/src/pages/Cart.jsx" data-component-line="67:6" data-component-id="client/src/pages/Cart.jsx:67:6">
        <h1 className="font-serif text-4xl font-bold text-gray-800 mb-8" data-component-path="client/src/pages/Cart.jsx" data-component-line="68:8" data-component-id="client/src/pages/Cart.jsx:68:8">Shopping Cart</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-component-path="client/src/pages/Cart.jsx" data-component-line="70:8" data-component-id="client/src/pages/Cart.jsx:70:8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-component-path="client/src/pages/Cart.jsx" data-component-line="72:10" data-component-id="client/src/pages/Cart.jsx:72:10">
            {cart.map(item => (
              <div key={item.id} className="bg-white rounded-lg shadow-sm p-6" data-component-path="client/src/pages/Cart.jsx" data-component-line="74:14" data-component-id="client/src/pages/Cart.jsx:74:14">
                <div className="flex gap-6" data-component-path="client/src/pages/Cart.jsx" data-component-line="75:16" data-component-id="client/src/pages/Cart.jsx:75:16">
                  {/* Product Image */}
                  <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden" data-component-path="client/src/pages/Cart.jsx" data-component-line="77:18" data-component-id="client/src/pages/Cart.jsx:77:18">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://images.pexels.com/photos/1454171/pexels-photo-1454171.jpeg?auto=compress&cs=tinysrgb&w=600'
                      }}
                      data-component-path="client/src/pages/Cart.jsx" data-component-line="78:20" data-component-id="client/src/pages/Cart.jsx:78:20"
                    />
                  </div>

                  {/* Product Details */}
                  <div className="flex-1" data-component-path="client/src/pages/Cart.jsx" data-component-line="89:18" data-component-id="client/src/pages/Cart.jsx:89:18">
                    <div className="flex justify-between items-start mb-2" data-component-path="client/src/pages/Cart.jsx" data-component-line="90:20" data-component-id="client/src/pages/Cart.jsx:90:20">
                      <div data-component-path="client/src/pages/Cart.jsx" data-component-line="91:22" data-component-id="client/src/pages/Cart.jsx:91:22">
                        <h3 className="font-serif text-lg font-semibold text-gray-800" data-component-path="client/src/pages/Cart.jsx" data-component-line="92:24" data-component-id="client/src/pages/Cart.jsx:92:24">
                          {item.name}
                        </h3>
                        <p className="text-sm text-gray-600" data-component-path="client/src/pages/Cart.jsx" data-component-line="95:24" data-component-id="client/src/pages/Cart.jsx:95:24">{item.category}</p>
                        {item.description && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1" data-component-path="client/src/pages/Cart.jsx" data-component-line="97:26" data-component-id="client/src/pages/Cart.jsx:97:26">
                            {item.description}
                          </p>
                        )}
                      </div>
                      <button 
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-700 transition-colors p-2"
                        data-component-path="client/src/pages/Cart.jsx" data-component-line="103:22" data-component-id="client/src/pages/Cart.jsx:103:22"
                      >
                        <Trash2 className="w-5 h-5" data-component-path="client/src/pages/Cart.jsx" data-component-line="107:24" data-component-id="client/src/pages/Cart.jsx:107:24" />
                      </button>
                    </div>

                    <div className="flex items-center justify-between mt-4" data-component-path="client/src/pages/Cart.jsx" data-component-line="111:20" data-component-id="client/src/pages/Cart.jsx:111:20">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-3" data-component-path="client/src/pages/Cart.jsx" data-component-line="113:22" data-component-id="client/src/pages/Cart.jsx:113:22">
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                          data-component-path="client/src/pages/Cart.jsx" data-component-line="114:24" data-component-id="client/src/pages/Cart.jsx:114:24"
                        >
                          <Minus className="w-4 h-4 text-gray-600" data-component-path="client/src/pages/Cart.jsx" data-component-line="118:26" data-component-id="client/src/pages/Cart.jsx:118:26" />
                        </button>
                        <span className="text-gray-800 font-medium w-8 text-center" data-component-path="client/src/pages/Cart.jsx" data-component-line="120:24" data-component-id="client/src/pages/Cart.jsx:120:24">
                          {item.quantity}
                        </span>
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                          data-component-path="client/src/pages/Cart.jsx" data-component-line="123:24" data-component-id="client/src/pages/Cart.jsx:123:24"
                        >
                          <Plus className="w-4 h-4 text-gray-600" data-component-path="client/src/pages/Cart.jsx" data-component-line="127:26" data-component-id="client/src/pages/Cart.jsx:127:26" />
                        </button>
                      </div>

                      {/* Price */}
                      <div className="text-right" data-component-path="client/src/pages/Cart.jsx" data-component-line="132:22" data-component-id="client/src/pages/Cart.jsx:132:22">
                        <p className="text-xl font-bold text-champagne-600" data-component-path="client/src/pages/Cart.jsx" data-component-line="133:24" data-component-id="client/src/pages/Cart.jsx:133:24">
                          ₹{(item.price * item.quantity).toLocaleString()}
                        </p>
                        {item.quantity > 1 && (
                          <p className="text-xs text-gray-500" data-component-path="client/src/pages/Cart.jsx" data-component-line="137:26" data-component-id="client/src/pages/Cart.jsx:137:26">
                            ₹{item.price.toLocaleString()} each
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1" data-component-path="client/src/pages/Cart.jsx" data-component-line="151:10" data-component-id="client/src/pages/Cart.jsx:151:10">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24" data-component-path="client/src/pages/Cart.jsx" data-component-line="152:12" data-component-id="client/src/pages/Cart.jsx:152:12">
              <h2 className="font-serif text-2xl font-bold text-gray-800 mb-6" data-component-path="client/src/pages/Cart.jsx" data-component-line="153:14" data-component-id="client/src/pages/Cart.jsx:153:14">Order Summary</h2>
              
              {/* WhatsApp Notice */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6" data-component-path="client/src/pages/Cart.jsx" data-component-line="156:14" data-component-id="client/src/pages/Cart.jsx:156:14">
                <div className="flex items-start gap-3" data-component-path="client/src/pages/Cart.jsx" data-component-line="157:16" data-component-id="client/src/pages/Cart.jsx:157:16">
                  <MessageCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-component-path="client/src/pages/Cart.jsx" data-component-line="158:18" data-component-id="client/src/pages/Cart.jsx:158:18" />
                  <div data-component-path="client/src/pages/Cart.jsx" data-component-line="159:18" data-component-id="client/src/pages/Cart.jsx:159:18">
                    <p className="text-sm font-semibold text-green-800 mb-1" data-component-path="client/src/pages/Cart.jsx" data-component-line="160:20" data-component-id="client/src/pages/Cart.jsx:160:20">
                      Orders via WhatsApp
                    </p>
                    <p className="text-xs text-green-700" data-component-path="client/src/pages/Cart.jsx" data-component-line="163:20" data-component-id="client/src/pages/Cart.jsx:163:20">
                      We accept orders through WhatsApp for a personalized shopping experience
                    </p>
                  </div>
                </div>
              </div>

              {/* Delivery Policy */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-component-path="client/src/pages/Cart.jsx" data-component-line="171:14" data-component-id="client/src/pages/Cart.jsx:171:14">
                <div className="flex items-start gap-3" data-component-path="client/src/pages/Cart.jsx" data-component-line="172:16" data-component-id="client/src/pages/Cart.jsx:172:16">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" data-component-path="client/src/pages/Cart.jsx" data-component-line="173:18" data-component-id="client/src/pages/Cart.jsx:173:18" />
                  <div data-component-path="client/src/pages/Cart.jsx" data-component-line="174:18" data-component-id="client/src/pages/Cart.jsx:174:18">
                    <p className="text-sm font-semibold text-blue-800 mb-1" data-component-path="client/src/pages/Cart.jsx" data-component-line="175:20" data-component-id="client/src/pages/Cart.jsx:175:20">
                      Free Delivery
                    </p>
                    <p className="text-xs text-blue-700" data-component-path="client/src/pages/Cart.jsx" data-component-line="178:20" data-component-id="client/src/pages/Cart.jsx:178:20">
                      Enjoy free delivery on orders with up to 3 products. Additional shipping charges apply for larger orders.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-6" data-component-path="client/src/pages/Cart.jsx" data-component-line="186:14" data-component-id="client/src/pages/Cart.jsx:186:14">
                <div className="flex justify-between text-gray-600" data-component-path="client/src/pages/Cart.jsx" data-component-line="187:16" data-component-id="client/src/pages/Cart.jsx:187:16">
                  <span data-component-path="client/src/pages/Cart.jsx" data-component-line="188:18" data-component-id="client/src/pages/Cart.jsx:188:18">Subtotal ({cartCount} {cartCount === 1 ? 'item' : 'items'})</span>
                  <span className="font-medium" data-component-path="client/src/pages/Cart.jsx" data-component-line="189:18" data-component-id="client/src/pages/Cart.jsx:189:18">₹{cartTotal.toLocaleString()}</span>
                </div>
                
                <div className="flex justify-between text-gray-600" data-component-path="client/src/pages/Cart.jsx" data-component-line="192:16" data-component-id="client/src/pages/Cart.jsx:192:16">
                  <span data-component-path="client/src/pages/Cart.jsx" data-component-line="193:18" data-component-id="client/src/pages/Cart.jsx:193:18">Shipping</span>
                  <span className="font-medium" data-component-path="client/src/pages/Cart.jsx" data-component-line="194:18" data-component-id="client/src/pages/Cart.jsx:194:18">
                    {shipping === 0 ? (
                      <span className="text-green-600 font-semibold" data-component-path="client/src/pages/Cart.jsx" data-component-line="196:22" data-component-id="client/src/pages/Cart.jsx:196:22">FREE</span>
                    ) : (
                      `₹${shipping}`
                    )}
                  </span>
                </div>

                {shipping > 0 && (
                  <p className="text-xs text-gray-500" data-component-path="client/src/pages/Cart.jsx" data-component-line="204:18" data-component-id="client/src/pages/Cart.jsx:204:18">
                    Remove {cartCount - 3} {cartCount - 3 === 1 ? 'item' : 'items'} for free shipping
                  </p>
                )}
                
                <div className="flex justify-between text-gray-600" data-component-path="client/src/pages/Cart.jsx" data-component-line="209:16" data-component-id="client/src/pages/Cart.jsx:209:16">
                  <span data-component-path="client/src/pages/Cart.jsx" data-component-line="210:18" data-component-id="client/src/pages/Cart.jsx:210:18">Tax (GST 3%)</span>
                  <span className="font-medium" data-component-path="client/src/pages/Cart.jsx" data-component-line="211:18" data-component-id="client/src/pages/Cart.jsx:211:18">₹{tax.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                </div>

                <div className="border-t border-gray-200 pt-4" data-component-path="client/src/pages/Cart.jsx" data-component-line="214:16" data-component-id="client/src/pages/Cart.jsx:214:16">
                  <div className="flex justify-between items-center" data-component-path="client/src/pages/Cart.jsx" data-component-line="215:18" data-component-id="client/src/pages/Cart.jsx:215:18">
                    <span className="font-serif text-xl font-bold text-gray-800" data-component-path="client/src/pages/Cart.jsx" data-component-line="216:20" data-component-id="client/src/pages/Cart.jsx:216:20">Total</span>
                    <span className="font-serif text-2xl font-bold text-champagne-600" data-component-path="client/src/pages/Cart.jsx" data-component-line="217:20" data-component-id="client/src/pages/Cart.jsx:217:20">
                      ₹{total.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                </div>
              </div>

              <button 
                onClick={handleWhatsAppCheckout}
                className="w-full bg-green-600 text-white h-14 rounded-full hover:bg-green-700 transition-all duration-200 shadow-md hover:shadow-lg font-medium text-lg flex items-center justify-center gap-2"
                data-component-path="client/src/pages/Cart.jsx" data-component-line="224:14" data-component-id="client/src/pages/Cart.jsx:224:14"
              >
                <MessageCircle className="w-5 h-5" data-component-path="client/src/pages/Cart.jsx" data-component-line="228:16" data-component-id="client/src/pages/Cart.jsx:228:16" />
                Checkout via WhatsApp
              </button>

              <p className="text-xs text-center text-gray-500 mt-3" data-component-path="client/src/pages/Cart.jsx" data-component-line="232:14" data-component-id="client/src/pages/Cart.jsx:232:14">
                You'll be redirected to WhatsApp to complete your order
              </p>

              <Link 
                to="/shop"
                className="block text-center text-champagne-600 hover:text-champagne-700 font-medium mt-4 transition-colors"
                data-component-path="client/src/pages/Cart.jsx" data-component-line="237:14" data-component-id="client/src/pages/Cart.jsx:237:14"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Cart
